import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  MessageCircle, 
  Bot, 
  TrendingUp, 
  Package, 
  Users, 
  DollarSign, 
  BarChart3, 
  Zap,
  Sparkles,
  ArrowRight,
  X,
  Mic,
  MicOff,
  Send,
  Clock,
  Star,
  Loader2,
  ChevronDown,
  Minimize2,
  Volume2,
  VolumeX
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAiAccess } from '../../hooks/useAiAccess';
import firebaseAIService from '../../services/firebaseAIService';
import businessIntelligenceService from '../../services/businessIntelligenceService';
import { dataService, ChatMessage as DBChatMessage, ChatSession } from '../../services/dataService';

// Type declarations for speech recognition
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isTyping?: boolean;
  isMuted?: boolean;
}

const BusinessChatbot: React.FC = () => {
  const { tenantData } = useAuth();
  const { hasAccess, isLoading: accessLoading, error: accessError } = useAiAccess();
  const location = useLocation();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showQuickQuestions, setShowQuickQuestions] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [businessData, setBusinessData] = useState<any>(null);
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState<any>(null);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const speechSynthesis = window.speechSynthesis;

  const features = [
    {
      icon: TrendingUp,
      title: 'Sales Analytics',
      description: 'Get real-time insights into your daily sales, revenue trends, and performance metrics.',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Package,
      title: 'Inventory Management',
      description: 'Track stock levels, get low stock alerts, and manage your inventory efficiently.',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: Users,
      title: 'Customer Insights',
      description: 'Understand your customers better with spending patterns and behavior analytics.',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: BarChart3,
      title: 'Business Intelligence',
      description: 'Advanced analytics and reports to make data-driven business decisions.',
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const sampleQueries = [
    "What are today's sales?",
    "Which items are selling best?",
    "Show me low stock items",
    "Who are our top customers?",
    "What's our revenue trend?"
  ];

  // Pages where the floating button should be visible
  const pagesWithFloatingButton = [
    '/dashboard',
    '/subscription',
    '/settings',
    '/features'
  ];

  const shouldShowFloatingButton = () => {
    return pagesWithFloatingButton.includes(location.pathname);
  };

  // Load business data and chat history when component mounts
  useEffect(() => {
    if (tenantData?.id && isOpen) {
      loadBusinessData();
      loadChatHistory();
    }
  }, [tenantData?.id, isOpen]);

  // Initialize chat session when component mounts
  useEffect(() => {
    if (tenantData?.id && isOpen && !currentSessionId) {
      initializeChatSession();
    }
  }, [tenantData?.id, isOpen, currentSessionId]);

  // Listen for open chatbot event
  useEffect(() => {
    const handleOpenChatbot = () => {
      setIsOpen(true);
      setIsMinimized(false);
    };

    window.addEventListener('open-chatbot', handleOpenChatbot);
    return () => window.removeEventListener('open-chatbot', handleOpenChatbot);
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';
      
      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInputValue(transcript);
        setIsListening(false);
      };
      
      recognition.onerror = (event: any) => {
        setIsListening(false);
      };
      
      recognition.onend = () => {
        setIsListening(false);
      };
      
      setRecognition(recognition);
    } else {
      // Speech recognition not supported
    }
    
    // Check speech synthesis
    if ('speechSynthesis' in window) {
      // Speech synthesis available
    } else {
      // Speech synthesis not supported
    }
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadBusinessData = async () => {
    if (!tenantData?.id) {
      return;
    }
    
    try {
      const data = await firebaseAIService.getBusinessData(tenantData.id);
      setBusinessData(data);
    } catch (error) {
      // Handle error silently in production
    }
  };



  const loadChatHistory = async () => {
    if (!tenantData?.id) return;
    
    setIsLoadingHistory(true);
    try {
      const sessions = await dataService.getChatSessions(tenantData.id);
      setChatSessions(sessions);
    } catch (error) {
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Helper function to ensure session exists
  const ensureSessionExists = async (sessionId: string): Promise<boolean> => {
    if (!tenantData?.id) return false;
    
    try {
      // Try to get the session to check if it exists
      await dataService.getById(tenantData.id, 'chatSessions', sessionId);
      return true;
    } catch (error) {
      // Session doesn't exist, create it
      try {
        const sessionData: Omit<ChatSession, 'id' | 'createdAt' | 'updatedAt'> = {
          sessionId,
          title: `Chat Session ${new Date().toLocaleDateString()}`,
          messageCount: 0,
          lastMessageAt: new Date(),
          isActive: true
        };
        await dataService.createChatSession(tenantData.id, sessionData);
        return true;
      } catch (createError) {
        console.error('Failed to create missing session:', createError);
        return false;
      }
    }
  };

  const initializeChatSession = async () => {
    if (!tenantData?.id) return;
    
    try {
      // Check if there's an active session
      let activeSession = await dataService.getActiveChatSession(tenantData.id);
      
      if (!activeSession) {
        // Create new session
        const sessionId = await dataService.generateSessionId();
        const sessionData: Omit<ChatSession, 'id' | 'createdAt' | 'updatedAt'> = {
          sessionId,
          title: `Chat Session ${new Date().toLocaleDateString()}`,
          messageCount: 0,
          lastMessageAt: new Date(),
          isActive: true
        };
        
        try {
          await dataService.createChatSession(tenantData.id, sessionData);
          activeSession = { id: sessionId, ...sessionData } as ChatSession;
        } catch (createError) {
          console.error('Failed to create chat session:', createError);
          // Don't set activeSession if creation failed
          return;
        }
      }
      
      setCurrentSessionId(activeSession.sessionId);
      
      // Load messages for this session
      const sessionMessages = await dataService.getChatMessages(tenantData.id, activeSession.sessionId);
      
      if (sessionMessages.length === 0) {
        // Add welcome message for new sessions
        const welcomeMessage: ChatMessage = {
          id: 'welcome',
          role: 'assistant',
          content: "Hello! I'm your AI business assistant. I can help you with sales analytics, inventory management, customer insights, and much more! 🚀\n\nTry asking me questions like:\n• What are today's sales?\n• Which items are selling best?\n• Show me low stock items\n• Who are our top customers?",
          timestamp: new Date()
        };
        setMessages([welcomeMessage]);
      } else {
        // Convert DB messages to UI format
        const uiMessages: ChatMessage[] = sessionMessages.map(msg => ({
          id: msg.id || '',
          role: msg.role,
          content: msg.content,
          timestamp: (() => {
            try {
              // Handle Firestore Timestamp objects
              if (msg.timestamp && typeof msg.timestamp === 'object' && 'toDate' in msg.timestamp) {
                return (msg.timestamp as any).toDate();
              }
              // Handle regular Date objects
              if (msg.timestamp instanceof Date) {
                return msg.timestamp;
              }
              // Handle string timestamps
              if (typeof msg.timestamp === 'string' || typeof msg.timestamp === 'number') {
                return new Date(msg.timestamp);
              }
              // Fallback to current date
              return new Date();
            } catch (error) {
              return new Date();
            }
          })()
        }));
        setMessages(uiMessages);
      }
      
    } catch (error) {
    }
  };

  const loadChatSession = async (sessionId: string) => {
    if (!tenantData?.id) return;
    
    try {
      const sessionMessages = await dataService.getChatMessages(tenantData.id, sessionId);
      const uiMessages: ChatMessage[] = sessionMessages.map(msg => ({
        id: msg.id || '',
        role: msg.role,
        content: msg.content,
        timestamp: (() => {
          try {
            // Handle Firestore Timestamp objects
            if (msg.timestamp && typeof msg.timestamp === 'object' && 'toDate' in msg.timestamp) {
              return (msg.timestamp as any).toDate();
            }
            // Handle regular Date objects
            if (msg.timestamp instanceof Date) {
              return msg.timestamp;
            }
            // Handle string timestamps
            if (typeof msg.timestamp === 'string' || typeof msg.timestamp === 'number') {
              return new Date(msg.timestamp);
            }
            // Fallback to current date
            return new Date();
          } catch (error) {
            console.error('Error converting timestamp:', error);
            return new Date();
          }
        })()
      }));
      
      setMessages(uiMessages);
      setCurrentSessionId(sessionId);
      setShowHistory(false);
      
    } catch (error) {
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || !tenantData?.id || !hasAccess) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);



    try {
      // Load fresh business data for each query
      const freshBusinessData = await businessIntelligenceService.getBusinessData(tenantData.id);
      
      // Add session ID for rate limiting
      const businessDataWithSession = {
        ...freshBusinessData,
        sessionId: currentSessionId
      };
      
      // Get AI response
      const aiResponse = await firebaseAIService.processBusinessQuery(
        inputValue,
        tenantData.id,
        businessDataWithSession
      );

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: aiResponse.content,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Save messages to database
      if (tenantData?.id && currentSessionId) {
        try {
          // Save user message
          await dataService.saveChatMessage(tenantData.id, {
            role: 'user',
            content: inputValue,
            timestamp: new Date(),
            sessionId: currentSessionId,
            queryType: 'business_query'
          });

          // Save assistant message
          await dataService.saveChatMessage(tenantData.id, {
            role: 'assistant',
            content: aiResponse.content,
            timestamp: new Date(),
            sessionId: currentSessionId,
            queryType: 'ai_response',
            dataUsed: aiResponse.dataUsed,
            confidence: aiResponse.confidence,
            suggestions: aiResponse.suggestions
          });

          // Update session with correct message count
          const currentMessageCount = messages.length + 2; // +2 for user and assistant messages
          
          // Ensure session exists before updating
          const sessionExists = await ensureSessionExists(currentSessionId);
          if (sessionExists) {
            try {
              await dataService.updateChatSession(tenantData.id, currentSessionId, {
                messageCount: currentMessageCount,
                lastMessageAt: new Date()
              });
            } catch (sessionError) {
              console.error('Failed to update chat session:', sessionError);
            }
          }

        } catch (dbError) {
          // Continue even if database save fails - messages are still shown in UI
        }
      }
    } catch (error) {
      
      let errorMessage = "I'm having trouble processing your request right now. Please try again in a moment.";
      
      if (error instanceof Error) {
        if (error.message.includes('API key') || error.message.includes('OpenAI')) {
          errorMessage = "I'm having trouble connecting to the AI service. Please check your API configuration.";
        } else if (error.message.includes('business data') || error.message.includes('Firebase')) {
          errorMessage = "I'm having trouble accessing your business data. Please check your connection and try again.";
        } else {
          errorMessage = `Error: ${error.message}`;
        }
      }
      
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: errorMessage,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Voice input functions
  const startListening = () => {
    if (recognition) {
      recognition.start();
      setIsListening(true);
    }
  };

  const stopListening = () => {
    if (recognition) {
      recognition.stop();
      setIsListening(false);
    }
  };



  const toggleMessageVoice = (messageId: string, content: string) => {
    setMessages(prev => prev.map(msg => {
      if (msg.id === messageId) {
        const newMutedState = !msg.isMuted;
        
        // If unmuting, speak the message
        if (!newMutedState && speechSynthesis) {
          const utterance = new SpeechSynthesisUtterance(content);
          utterance.rate = 0.9;
          utterance.pitch = 1;
          speechSynthesis.speak(utterance);
        }
        
        return { ...msg, isMuted: newMutedState };
      }
      return msg;
    }));
  };



  // Auto-resize textarea
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    
    // Auto-resize
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  if (!isOpen) {
    // Don't show floating button if not on allowed pages
    if (!shouldShowFloatingButton()) {
      return null;
    }

    return (
      <div className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] right-2 tablet:bottom-16 tablet:right-4 z-50">
        <button
          onClick={() => setIsOpen(true)}
          className="group relative bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white p-4 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 hover:rotate-3"
        >
          <Bot className="w-6 h-6" />
          
          {/* Pulse animation */}
          <div className="absolute inset-0 rounded-full bg-primary-400 animate-ping opacity-20"></div>
          
          {/* Glow effect */}
          <div className="absolute inset-0 rounded-full bg-primary-400 blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
          
          {/* Status indicator */}
          <div className="absolute -top-1 -right-1 bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse border-2 border-white">
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
        </button>
        
        {/* Tooltip */}
        <div className="absolute bottom-full right-0 mb-3 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">AI Business Assistant</span>
              <div className="flex items-center space-x-1 mt-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-600 dark:text-green-400 font-medium">Live</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">Get instant insights about your business with AI-powered analytics.</p>
          <div className="flex items-center justify-between">
            <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">Click to start chatting</span>
            <div className="flex items-center space-x-2">
              <Sparkles className="w-3 h-3 text-primary-500" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] right-2 tablet:bottom-16 tablet:right-4 z-50 max-h-[calc(100vh-7.5rem)] tablet:max-h-[calc(100vh-8rem)]">
      <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col ${
        isMinimized ? 'w-80 h-16' : 'w-[95vw] sm:w-[420px] max-h-[calc(100vh-6rem)] sm:max-h-[calc(100vh-8rem)] h-[600px]'
      }`}>
        
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white p-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Bot className="w-6 h-6" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            </div>
            <div>
              <h3 className="font-semibold">AI Business Assistant</h3>
              <p className="text-xs text-primary-100">Live</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title="Chat History"
            >
              <Clock className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title={isMinimized ? "Expand" : "Minimize"}
            >
              {isMinimized ? <MessageCircle className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Access Control Check */}
            {accessLoading ? (
              <div className="bg-gradient-to-r from-blue-400 to-blue-600 text-white p-3 text-center flex-shrink-0">
                <div className="flex items-center justify-center space-x-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm font-medium">Checking AI access...</span>
                </div>
              </div>
            ) : !hasAccess ? (
              <div className="bg-gradient-to-r from-red-400 to-red-600 text-white p-4 text-center flex-shrink-0">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Bot className="w-5 h-5" />
                  <span className="text-sm font-medium">AI Chatbot Access Required</span>
                </div>
                <p className="text-xs opacity-90 mb-3">
                  This feature is available for Professional and Enterprise plans only.
                </p>
                <div className="bg-white/20 rounded-lg p-3 text-xs">
                  <p className="font-medium mb-1">Upgrade to Professional Plan to get:</p>
                  <ul className="space-y-1 text-left">
                    <li>• 🤖 AI Business Assistant</li>
                    <li>• 📊 Advanced Analytics</li>
                    <li>• 🔗 API Access</li>
                    <li>• 🎯 Priority Support</li>
                  </ul>
                </div>
              </div>
            ) : (
              <>
                {/* AI Assistant Status */}
                <div className="bg-gradient-to-r from-green-400 to-green-600 text-white p-3 text-center relative overflow-hidden flex-shrink-0">
                  <div className="flex items-center justify-center space-x-2 relative z-10">
                    <Bot className="w-4 h-4" />
                    <span className="text-sm font-medium">🤖 AI Assistant is Live!</span>
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  </div>
                  {/* Animated background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
                </div>

                {/* Chat History Panel */}
                {showHistory && (
                  <div className="bg-gray-100 dark:bg-gray-700 p-4 border-b border-gray-200 dark:border-gray-600 flex-shrink-0">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Chat History</h4>
                      <button
                        onClick={() => setShowHistory(false)}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {isLoadingHistory ? (
                        <div className="text-center text-sm text-gray-500">Loading history...</div>
                      ) : chatSessions.length > 0 ? (
                        chatSessions.map((session) => (
                          <button
                            key={session.sessionId}
                            onClick={() => loadChatSession(session.sessionId)}
                            className={`w-full text-left p-2 rounded text-xs transition-colors ${
                              session.sessionId === currentSessionId
                                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                                : 'hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            <div className="font-medium">{session.title}</div>
                            <div className="text-gray-500 dark:text-gray-400">
                              {session.messageCount} messages • {new Date(session.lastMessageAt).toLocaleDateString()}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="text-center text-sm text-gray-500">No chat history yet</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Chat Messages - Now properly flexible */}
                <div className="flex-1 p-4 space-y-4 overflow-y-auto bg-gray-50/50 dark:bg-gray-700/50 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent min-h-0">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[280px] px-4 py-3 rounded-2xl text-sm leading-relaxed relative ${
                          message.role === 'user'
                            ? 'bg-primary-500 text-white shadow-lg'
                            : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-md border border-gray-100 dark:border-gray-600'
                        }`}
                      >
                        <div className="whitespace-pre-wrap break-words">{message.content}</div>
                        <div className="text-xs opacity-70 mt-2">
                          {(() => {
                            try {
                              const timestamp = message.timestamp instanceof Date 
                                ? message.timestamp 
                                : new Date(message.timestamp);
                              
                              if (isNaN(timestamp.getTime())) {
                                return 'Invalid Date';
                              }
                              
                              return timestamp.toLocaleTimeString('en-US', { 
                                hour: '2-digit', 
                                minute: '2-digit',
                                hour12: true 
                              });
                            } catch (error) {
                              console.error('Error formatting timestamp:', error);
                              return 'Invalid Date';
                            }
                          })()}
                        </div>
                        {/* Voice button for assistant messages */}
                        {message.role === 'assistant' && (
                          <button
                            onClick={() => toggleMessageVoice(message.id, message.content)}
                            className={`absolute bottom-2 right-2 p-1.5 rounded-full transition-colors shadow-sm ${
                              message.isMuted 
                                ? 'bg-gray-400 text-white hover:bg-gray-500' 
                                : 'bg-primary-500 text-white hover:bg-primary-600'
                            }`}
                            title={message.isMuted ? "Unmute and listen" : "Mute this message"}
                          >
                            {message.isMuted ? (
                              <VolumeX className="w-3 h-3" />
                            ) : (
                              <Volume2 className="w-3 h-3" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-md border border-gray-100 dark:border-gray-600 px-4 py-3 rounded-2xl">
                        <div className="flex items-center space-x-2">
                          <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
                          <span className="text-sm text-gray-600 dark:text-gray-300">AI is thinking...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Quick Actions - Now properly positioned */}
                {showQuickQuestions && (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-700/50 flex-shrink-0">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                        Quick Questions
                      </h4>
                      <button
                        onClick={() => setShowQuickQuestions(false)}
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors p-1"
                        title="Hide quick questions"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {sampleQueries.slice(0, 4).map((query, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setInputValue(query);
                            setTimeout(() => handleSendMessage(), 100);
                          }}
                          disabled={isLoading || !hasAccess}
                          className="text-left p-2 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                        >
                          <p className="text-xs text-gray-700 dark:text-gray-300 font-medium">{query}</p>
                        </button>
                      ))}
                    </div>
                    

                  </div>
                )}

                {/* Input Area - Now properly positioned at bottom */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
                  <div className="flex items-end space-x-3">
                    <div className="flex-1 relative">
                      <textarea
                        value={inputValue}
                        onChange={handleTextareaChange}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        placeholder={hasAccess ? "Ask about your business..." : "Upgrade to Professional Plan to use AI Chatbot"}
                        rows={1}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                        disabled={isLoading || !hasAccess}
                        style={{ minHeight: '44px', maxHeight: '120px' }}
                      />
                      {isLoading && (
                        <div className="absolute right-3 bottom-3">
                          <Loader2 className="w-4 h-4 text-primary-500 animate-spin" />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-row space-x-2">
                        {/* Microphone Button (main action) */}
                        <button
                          onClick={isListening ? stopListening : startListening}
                          disabled={isLoading || !recognition}
                          className={`p-3 rounded-xl transition-all duration-200 ${
                            isListening 
                              ? 'bg-red-500 text-white animate-pulse' 
                              : recognition
                              ? 'bg-blue-500 text-white hover:bg-blue-600'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                          title={isListening ? "Stop listening" : recognition ? "Start voice input" : "Voice not supported"}
                        >
                          {isListening ? (
                            <MicOff className="w-5 h-5" />
                          ) : (
                            <Mic className="w-5 h-5" />
                          )}
                        </button>
                        {/* Send Button (secondary) */}
                        <button
                          onClick={handleSendMessage}
                          disabled={!inputValue.trim() || isLoading || !hasAccess}
                          className="p-3 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl ml-1"
                          title="Send message"
                          style={{ minWidth: 44, minHeight: 44 }}
                        >
                          {isLoading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center space-x-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        💡 Try: "What are today's sales?"
                      </p>
                      {!showQuickQuestions && (
                        <button
                          onClick={() => setShowQuickQuestions(true)}
                          className="text-xs text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 transition-colors flex items-center space-x-1"
                          title="Show quick questions"
                        >
                          <Sparkles className="w-3 h-3" />
                          <span>Quick Questions</span>
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => setInputValue('')}
                      className="text-xs text-gray-500 hover:text-primary-500 transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessChatbot; 